import CropFreeIcon from "@mui/icons-material/CropFree";
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { BarcodeScannerDialog } from "components/barcodeScan";
import StyledTextField from "components/StyledTextField";
import { scanditLicense } from "env";
import { filterPartList, intlFormatPrice } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { nanoid } from "nanoid";
import { PartType, StockStore } from "operations/schema/schema";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface PartAutocompleteProps {
  selectedPart: PartType | null;
  partList: PartType[];
  loading: boolean;
  showQty: boolean;
  partSearch: string;
  setPartSearch: Dispatch<SetStateAction<string>>;
  setScannerValue: Dispatch<SetStateAction<string>>;
  changeSelectedPart: (selectedPart: PartType | null) => void;
  disableBarcodeScan?: boolean;
}

export const PartAutocomplete: FC<PartAutocompleteProps> = (props) => {
  const {
    selectedPart,
    partList,
    loading,
    showQty,
    partSearch,
    setPartSearch,
    changeSelectedPart,
    setScannerValue,
    disableBarcodeScan,
  } = props;
  const intl = useIntl();
  const allowSalesPrice = technicianSettings.syncSalesPrice || technicianSettings.syncCostPrice;
  const hasBarcodeScanner = scanditLicense() && !disableBarcodeScan;
  const [openScanner, setOpenScanner] = useState(false);

  return (
    <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
      <Autocomplete
        fullWidth
        data-testid="PartAutocomplete"
        options={partList}
        loading={loading}
        groupBy={(part) => {
          const stockStoreTranslatable =
            part.stockStore === StockStore.Engineer
              ? intl.formatMessage({ id: "general.engineer" })
              : part.stockStore === StockStore.Locational
                ? intl.formatMessage({ id: "part.locational" })
                : part.stockStore === StockStore.Nonstock
                  ? intl.formatMessage({ id: "part.nonStock" })
                  : part.stockStore === StockStore.Other
                    ? intl.formatMessage({ id: "part.other" })
                    : part.stockStore;

          return part.stockStore === StockStore.Locational
            ? (part.stockName?.toUpperCase() ?? "")
            : (stockStoreTranslatable?.toUpperCase() ?? "");
        }}
        getOptionLabel={(part: PartType) => {
          let label = part.description;

          if (showQty && !part.isNonStock) {
            label += ` (${part.quantity})`;
          }
          return label ?? "";
        }}
        noOptionsText={intl.formatMessage({
          id: "general.noOptions",
        })}
        filterOptions={(options: PartType[], state: FilterOptionsState<PartType>) => {
          return filterPartList(options, state.inputValue);
        }}
        value={selectedPart}
        inputValue={partSearch}
        onInputChange={(_, value) => {
          setPartSearch(value);
        }}
        onChange={(_, value) => changeSelectedPart(value)}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label={intl.formatMessage({
              id: "part",
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, part: PartType) => (
          <li {...props} key={nanoid()} style={{ display: "block" }}>
            <Typography variant="body1">
              {part.description}
              {showQty && !part.isNonStock && ` (${part.quantity})`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {part.articleNo}
            </Typography>
            {allowSalesPrice && part.salesPrice && (
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="general.salesPrice" />:{" "}
                {intlFormatPrice(intl, part.salesPrice)}
              </Typography>
            )}
          </li>
        )}
      />
      {hasBarcodeScanner && (
        <IconButton onClick={() => setOpenScanner(true)} size="large">
          <CropFreeIcon />
        </IconButton>
      )}
      {hasBarcodeScanner && (
        <BarcodeScannerDialog
          open={openScanner}
          setOpen={(open: boolean) => setOpenScanner(open)}
          submitValue={(value: string) => {
            setScannerValue(value);
            setOpenScanner(false);
          }}
        />
      )}
    </Grid>
  );
};
